import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://campuslens.xyz';

/**
 * Get authorization header with JWT token
 */
const getAuthHeader = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = user?.access_token;
  if (!token) {
    throw new Error('No authentication token found');
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

/**
 * Admin Service - All API calls for admin user management
 * SECURITY: All endpoints require admin authentication
 */

/**
 * Get admin dashboard statistics
 * @returns {Promise} Stats for users in admin's domain
 */
export const getAdminStats = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/admin/stats`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    // Silently fail - expected for non-admin users
    throw error;
  }
};

/**
 * Get all users in admin's domain
 * @returns {Promise} List of users
 */
export const getAllUsers = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/admin/users`, {
      headers: getAuthHeader()
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Update user admin status
 * @param {number} userId - User ID to update
 * @param {boolean} isAdmin - Whether user should be admin
 * @param {string} adminDomain - Domain the admin can manage (optional)
 * @returns {Promise} Updated user data
 */
export const updateUserAdminStatus = async (userId, isAdmin, adminDomain = null) => {
  try {
    const response = await axios.put(
      `${API_BASE_URL}/api/admin/users/${userId}`,
      {
        is_admin: isAdmin,
        admin_domain: adminDomain
      },
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating user admin status:', error);
    throw error;
  }
};

/**
 * Delete a user
 * @param {number} userId - User ID to delete
 * @returns {Promise} Deletion confirmation
 */
export const deleteUser = async (userId) => {
  try {
    const response = await axios.delete(
      `${API_BASE_URL}/api/admin/users/${userId}`,
      {
        headers: getAuthHeader()
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Check if current user has admin privileges
 * @returns {Promise<boolean>} True if user is admin
 */
export const checkAdminStatus = async () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    const token = user?.access_token;
    if (!token) return false;

    // Use fetch instead of axios to avoid console errors for 403
    const response = await fetch(`${API_BASE_URL}/api/admin/stats`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.ok;
  } catch (error) {
    // Silently fail - expected for non-admin users
    return false;
  }
};
